import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Pill } from '../sso-workspace/pill'
import { FleetGrid, useGridList } from '../sso-workspace/fleet-grid'
import { TableAgClient } from '../table-builder/table-ag-client'
import { ColumnFormat, ColumnMenu, ColumnText } from '../table-builder/table-columns'
import { Menu, MenuItem } from '../menu/menu'
import { IconCancel, IconCopy, IconEmail, IconProvider } from '../icon/icon'
import { ConfirmDialog } from '../sso-workspace/confirm-dialog'
import { CopyValue } from '../sso-workspace/copy-value'
import { copyTextLater, manualCopyHint, selectText } from '../clipboard/clipboard'
import { ErrorReference } from '../error/error-reference'
import {
  CONSENT_STATUS_FILTERS,
  consentStatusLabel,
  consentStatusTone,
  directoryLabel,
  formatDate,
  isOpen,
  serverMessage,
  shortTenant
} from './consent-model'

// Consent requests as the shared AG Grid (table-builder): directory, who
// asked, where it went, status, dates, and the row actions -- Copy link,
// Resend and Cancel while it is waiting, Open connection once approved
// (staff only; org admins have no connection workspace).
//
// rows are the server's list rows; the status filter is applied here, so
// switching it never refetches. showOrganization adds the organization
// column for the staff-wide page.
export const ConsentRequestList = ({
  rows,
  loaded = true,
  enabled,
  showOrganization,
  showOpenConnection,
  initialFilter = '',
  emptyText,
  onLink,
  onResend,
  onCancel,
  onChanged
}) => {
  const [filter, setFilter] = useState(initialFilter)
  const [status, setStatus] = useState(null)
  const [error, setError] = useState(null)
  const [cause, setCause] = useState(null)
  const [cancelRow, setCancelRow] = useState(null)
  const [busy, setBusy] = useState(false)
  // The link, shown selected with Copy and Open, when the browser wouldn't
  // let it be copied: { label, url }.
  const [manual, setManual] = useState(null)
  const tableRef = useRef()
  const manualID = 'sc-requests-manual-link'

  useEffect(() => {
    if (manual) selectText(document.getElementById(manualID))
  }, [manual])

  const visible = useMemo(() => (filter ? rows.filter(r => r.status === filter) : rows), [rows, filter])

  const run = async (fn, success, failure) => {
    setError(null)
    setCause(null)
    setStatus(null)
    setManual(null)
    setBusy(true)
    try {
      await fn()
      if (success) setStatus(success)
      onChanged && (await onChanged())
    } catch (err) {
      setError(serverMessage(err, failure))
      setCause(err)
    } finally {
      setBusy(false)
    }
  }

  // Copying reads the link fresh (it is never kept in the list). Safari only
  // lets the click itself write the clipboard, and the link arrives after a
  // request: copyTextLater starts the write inside the click with the link
  // still loading (shared/clipboard). Nothing awaits before it. When the
  // browser refuses anyway, the link is shown selected, with Copy and Open.
  const handleCopy = row => {
    setError(null)
    setCause(null)
    setStatus(null)
    setManual(null)
    setBusy(true)
    return copyTextLater(onLink(row.id).then(result => result.reviewURL))
      .then(({ copied, text }) => {
        if (copied) setStatus(`Link for ${directoryLabel(row)} copied.`)
        else setManual({ label: directoryLabel(row), url: text })
      })
      .catch(err => {
        setError(serverMessage(err, "The link couldn't be loaded."))
        setCause(err)
      })
      .finally(() => setBusy(false))
  }

  const handleResend = row =>
    run(() => onResend(row.id), `Sent again to ${row.sentToEmail}.`, "The request couldn't be sent again.")

  const handleCancel = async () => {
    const row = cancelRow
    await run(() => onCancel(row.id), `Request for ${directoryLabel(row)} cancelled.`, "The request couldn't be cancelled.")
    setCancelRow(null)
  }

  // Display values flattened onto each row, so the grid's quick filter, sort
  // and CSV export work off what the row says. `actionsDisabled` rides on the
  // row too: a row action's disabled state then changes with the row data the
  // grid is handed, rather than depending on a cell re-render.
  const gridRows = useMemo(
    () =>
      Array.isArray(visible)
        ? visible.map(row => ({
            ...row,
            directory: directoryLabel(row),
            requestedBy: row.requestedByName || row.requestedByEmail || '',
            sentTo: row.sentToEmail || 'Link copied',
            statusLabel: consentStatusLabel(row.status),
            expires: row.status === 'pending' ? row.expiresAt : null,
            actionsDisabled: !enabled || busy
          }))
        : visible,
    [visible, enabled, busy]
  )
  const gridList = useGridList(loaded ? gridRows : undefined)

  const emptyState = {
    Icon: IconEmail,
    title: filter ? 'No matching requests' : 'No requests',
    message: filter ? `No requests are ${consentStatusLabel(filter).toLowerCase()}.` : emptyText
  }

  // The list always sits inside a section that names it ("Pending
  // approvals", "Requests"), so the grid is flush: the status select shares
  // the grid's toolbar row with its quick filter and menu.
  const statusSelect =
    <select className="form-select form-select-sm w-auto" aria-label="Filter requests by status" value={filter} onChange={e => setFilter(e.target.value)}>
      {CONSENT_STATUS_FILTERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>

  return (
    <div className="sc-requests">
      <p className="sw-status text-start mb-0" role="status">{status}</p>

      {error && <p className="text-danger small" role="alert">{error}</p>}
      {error && <ErrorReference error={cause} />}

      {manual &&
        <div className="sw-inline-panel mb-3">
          <p className="sw-hint mb-2" role="status">{`${manualCopyHint()}: your browser didn't let EducationXR copy it.`}</p>
          <CopyValue id={manualID} label={`Approval link for ${manual.label}`} value={manual.url} openHref={manual.url} />
        </div>}

      <FleetGrid title="consent requests" tableRef={tableRef} exportName="consent-requests" compact flush toolbar={statusSelect}>
        <TableAgClient ref={tableRef} apiList={gridList} sortCol="createdAt" sortDesc emptyState={emptyState}>
          {showOrganization && <ColumnText field="organizationName" header="Organization" sortable />}
          <ColumnFormat field="directory" header="Directory" render={directoryCell} sortable />
          <ColumnText field="requestedBy" header="Requested by" sortable />
          <ColumnFormat field="sentTo" header="Sent to" render={sentToCell} sortable />
          <ColumnFormat field="statusLabel" header="Status" render={statusCell} sortable />
          <ColumnFormat field="createdAt" header="Created" render={dateCell} sortable />
          <ColumnFormat field="expires" header="Expires" render={dateCell} sortable />
          <ColumnMenu
            menu={row =>
              <Menu small>
                <MenuItem Icon={IconCopy} text="copy link" pin show={isOpen(row)} disabled={row.actionsDisabled} onClick={() => handleCopy(row)} />
                <MenuItem Icon={IconEmail} text="resend" pin show={isOpen(row) && Boolean(row.sentToEmail)} disabled={row.actionsDisabled} onClick={() => handleResend(row)} />
                <MenuItem Icon={IconCancel} text="cancel request" pin show={isOpen(row)} disabled={row.actionsDisabled} onClick={() => setCancelRow(row)} />
                <MenuItem Icon={IconProvider} text="open connection" pin show={Boolean(showOpenConnection && row.status === 'approved' && row.providerID)} to={`/admin/sso/connections/${row.providerID}`} />
              </Menu>}
          />
        </TableAgClient>
      </FleetGrid>

      <ConfirmDialog
        open={!!cancelRow}
        title="Cancel this request?"
        body={cancelRow ? `The link for ${directoryLabel(cancelRow)} stops working. Anyone who opens it is told it was cancelled.` : ''}
        confirmText="Cancel request"
        busy={busy}
        onConfirm={handleCancel}
        onCancel={() => setCancelRow(null)}
      />
    </div>
  )
}

const directoryCell = row =>
  row.data
    ? <span>
        {row.data.tenantDomain || <span className="sw-mono" title={row.data.tenantID}>{shortTenant(row.data.tenantID)}</span>}
        {row.data.tenantDomain && <span className="sw-hint ms-2 sw-mono" title={row.data.tenantID}>{shortTenant(row.data.tenantID)}</span>}
      </span>
    : ''

const sentToCell = row => (row.data ? row.data.sentToEmail || <span className="sw-hint">Link copied</span> : '')

const statusCell = row => (row.data ? <Pill tone={consentStatusTone(row.data.status)}>{consentStatusLabel(row.data.status)}</Pill> : '')

const dateCell = row => formatDate(row.value)
